import { httpQuery } from '@luminovo/data-stack';
import { searchParam } from '@luminovo/design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod/v4';

import AssemblyOverviewPage from '@/areas/manufacturing-scenarios/ManufacturingScenarios/AssemblyOverview/AssemblyOverviewPage';
import { canViewProjectManufacturing } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// The navbar's link sets `forwardIfSingleAssembly`: a project with one assembly besides its BOM needs no overview, so
// it opens on that assembly's manufacturing, once it has one. The loader uses the overview's queries, so the overview
// doesn't ask again.
export const Route = createFileRoute('/_authenticated/next/projects/$projectId/manufacturing/')({
    staticData: { access: { permissions: canViewProjectManufacturing }, chrome: 'none' } satisfies PageStaticData,
    validateSearch: z.object({ forwardIfSingleAssembly: searchParam.boolean('optional') }),
    loaderDeps: ({ search }) => ({ forwardIfSingleAssembly: search.forwardIfSingleAssembly }),
    loader: async ({ context: { queryClient }, params: { projectId }, deps }) => {
        if (!deps.forwardIfSingleAssembly) {
            return;
        }
        const [{ items: assemblyIds }, { data: manufacturingDetails }] = await Promise.all([
            queryClient.ensureQueryData(
                httpQuery('GET /assemblies/:assemblyId/tree', { pathParams: { assemblyId: projectId } }),
            ),
            queryClient.ensureQueryData(
                httpQuery('GET /manufacturing-assembly-details', { queryParams: { rfq_id: projectId } }),
            ),
        ]);
        // The BOM is the assembly whose id is the project's.
        const assemblies = assemblyIds.filter((assemblyId) => assemblyId !== projectId);
        const details =
            assemblies.length === 1
                ? manufacturingDetails.find((detail) => detail.assembly_id === assemblies[0])
                : undefined;
        if (details !== undefined) {
            throw redirect({
                to: '/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId',
                params: { projectId, manufacturingAssemblyDetailsId: details.id },
                replace: true,
            });
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const pathParams = Route.useParams();
    return <AssemblyOverviewPage rfqId={pathParams.projectId} />;
}
